import React, { useMemo, forwardRef, useEffect } from 'react';
import 'core-js/modules/es.promise.js';
import 'core-js/modules/es.object.assign.js';
import { createHttpLink, ApolloLink, ApolloClient, InMemoryCache, ApolloProvider, gql, useMutation } from '@apollo/client';
import { v4 } from 'uuid';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import { CumulioDashboardComponent } from '@cumul.io/react-cumulio-dashboard';

/**
 * Destructure and transform to a string. (Not usefully,
 * the Redux store field is named "token" and one of its
 * fields is also called "token")
 */
function toAuthorizationScheme(_ref) {
  var {
    scheme,
    token
  } = _ref;
  return scheme + " " + token;
}
/**
 * Using the token field retrieved from the Redux store,
 * generate the value for a request Authorization header
 */
function getAuthorizationHeaderFor(token) {
  if (!token) {
    return null;
  }
  if (typeof token === "string") {
    /**
     *  A string means non-SSO (JWT)
     */
    return "JWT " + token;
  }
  /**
   *  An object means SSO (Bearer)
   */
  return toAuthorizationScheme(token);
}

var customFetch = (uri, options) => {
  if (options.body instanceof FormData) return fetch(uri, options);
  var body = JSON.parse(options.body);
  var {
    operationName
  } = body;
  var paramsArray = ["opname=" + encodeURIComponent(operationName)];
  var filteredVariablesString = paramsArray.join("&");
  return fetch(uri + "?" + filteredVariablesString, options);
};
function isServerError(networkError) {
  if (!networkError) return false;
  return "statusCode" in networkError;
}
function ApiProvider(props) {
  var {
    env: {
      GATEWAY_URL
    },
    authState
  } = props;
  var client = useMemo(() => {
    var httpLink = createHttpLink({
      uri: GATEWAY_URL + "/graphql",
      fetch: customFetch,
      headers: {
        "keep-alive": "true"
      }
    });
    var authLink = setContext((_, _ref) => {
      var _authState$me;
      var {
        headers
      } = _ref;
      // generate unique request id and setting it as Sentry tag and attaching it to every request
      var requestId = v4();
      var token = (authState == null ? void 0 : authState.token) || (authState == null ? void 0 : (_authState$me = authState.me) == null ? void 0 : _authState$me.token);
      // return the headers to the context so httpLink can read them
      return {
        headers: Object.assign({}, headers, {
          authorization: getAuthorizationHeaderFor(token),
          "X-Request-Id": requestId
        })
      };
    });
    var errorLink = onError(_ref2 => {
      var {
        networkError
      } = _ref2;
      if (!isServerError(networkError)) return;
      if ((networkError == null ? void 0 : networkError.statusCode) === 401) {
        props.handleError(networkError == null ? void 0 : networkError.message);
      }
    });
    var link = ApolloLink.from([errorLink, authLink, httpLink]);
    var client = new ApolloClient({
      link,
      cache: new InMemoryCache()
    });
    return client;
  }, [GATEWAY_URL, authState, props]);
  return /*#__PURE__*/React.createElement(ApolloProvider, {
    client: client
  }, props.children);
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  strings.raw = raw;
  return strings;
}

var _templateObject;
var generateDashboardTokenMutation = /*#__PURE__*/gql(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    mutation GenerateAnalyticsDashboardTokenMutation(\n        $integrationId: String!,\n        $metadata: [AnalyticsDashboardMetadata]\n    ) {\n        generateAnalyticsDashboardToken(\n            integrationId: $integrationId,\n            metadata: $metadata\n        ) {\n            analyticsAuth {\n                token\n                key\n            }\n        }\n    }\n"])));

var DARK_GREY = "#404446";
var AnalyticsDashboardWrapper = (_ref, ref) => {
  var _authData$analyticsAu, _authData$analyticsAu2;
  var {
    dashboardSlug,
    integrationId,
    metadata,
    onFilterChange,
    onExport,
    onLoad,
    onItemsRendered
  } = _ref;
  var [generateCumulAuthToken, {
    data,
    loading,
    error
  }] = useMutation(generateDashboardTokenMutation);
  useEffect(() => {
    generateCumulAuthToken({
      variables: {
        integrationId,
        metadata
      }
    });
  }, [integrationId, metadata]);
  if (loading) {
    return /*#__PURE__*/React.createElement("div", null);
  }
  if (error) {
    return /*#__PURE__*/React.createElement("div", null, "Service Error " + error);
  }
  var authData = data == null ? void 0 : data.generateAnalyticsDashboardToken;
  if (authData != null && (_authData$analyticsAu = authData.analyticsAuth) != null && _authData$analyticsAu.key && authData != null && (_authData$analyticsAu2 = authData.analyticsAuth) != null && _authData$analyticsAu2.token && dashboardSlug) {
    return /*#__PURE__*/React.createElement(CumulioDashboardComponent, {
      ref: ref,
      changedFilters: onFilterChange,
      exported: onExport,
      dashboardSlug: dashboardSlug,
      authKey: authData.analyticsAuth.key,
      authToken: authData.analyticsAuth.token,
      loaderSpinnerColor: DARK_GREY,
      load: onLoad,
      itemsRendered: onItemsRendered
    });
  }
  return /*#__PURE__*/React.createElement("div", null, "Service Error");
};
var AnalyticsDashboardWrapper$1 = /*#__PURE__*/forwardRef(AnalyticsDashboardWrapper);

var handleError = networkErrorMessage => {
  console.log("[GraphQL error]: Network error 401: " + networkErrorMessage);
};
var AnalyticsDashboard = _ref => {
  var {
    authState,
    dashboardSlug,
    env,
    metadata,
    onFilterChange,
    onExport,
    onLoad,
    onItemsRendered,
    dashboardRef
  } = _ref;
  return /*#__PURE__*/React.createElement(ApiProvider, {
    authState: authState,
    handleError: handleError,
    env: env
  }, /*#__PURE__*/React.createElement(AnalyticsDashboardWrapper$1, {
    ref: dashboardRef,
    onFilterChange: onFilterChange,
    onExport: onExport,
    dashboardSlug: dashboardSlug,
    integrationId: env.CUMULIO_INTEGRATION_ID,
    metadata: metadata,
    onLoad: onLoad,
    onItemsRendered: onItemsRendered
  }));
};

export default AnalyticsDashboard;
//# sourceMappingURL=analytics-ui.esm.js.map
